import { RadioCard } from '../../components/ui/RadioCard'
import { formatMoney } from '../cart/cart-money'
import { ShippingAccountPicker } from './ShippingAccountPicker'
import { defaultPrepayMethodId } from './checkout-domain'
import type { ShippingAccountView, ShippingMethodOption } from './checkout-domain'

/**
 * Est.-delivery cell copy. Matches the prototype's `dcFmt`
 * (`Fictiv Platform.dc.html:6716`): `Aug 23, 2026` — `month:'short'`,
 * `day:'numeric'`, `year:'numeric'`, NO weekday. Formats the raw
 * `estDeliveryDate` (an ISO `YYYY-MM-DD`, parsed at local midnight so the
 * calendar day never shifts across time zones); falls back to the server's
 * preformatted `estDeliveryLabel`, then an em dash.
 */
function formatEstDelivery(method: ShippingMethodOption): string {
  if (method.estDeliveryDate) {
    const parsed = new Date(`${method.estDeliveryDate}T00:00`)
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    }
  }
  return method.estDeliveryLabel ?? '—'
}

export interface ShippingMethodStepProps {
  readonly methods: readonly ShippingMethodOption[]
  readonly selectedId: string
  readonly onSelect: (id: string) => void
  readonly busy?: boolean
  /**
   * UID-128 (ADR-0053 §7 follow-up, Gate 5 code-review) — the carrier account number
   * carried FROM the cart's originating RFQ's own_account seed
   * (`CheckoutShippingSeed.shipAccountNumber`), rendered as an informational note while
   * the own-account method is selected. DISPLAY-ONLY: the checkout own_account
   * `ShippingMethod` the order actually places carries a server-resolved demo
   * carrier/account reference regardless (`checkout-derivation.ts` `toShippingMethod`) —
   * there is no wire field on checkout submission for a client-supplied account number, so
   * this is never sent anywhere; it only lets the buyer SEE the RFQ's account was carried
   * forward. `null`/`undefined` renders nothing (no seed, or the RFQ recorded no account).
   */
  readonly seededShipAccountNumber?: string | null
  /**
   * UID-206 — the tenant's saved shipping accounts (own-account carrier book).
   * Rendered as the selectable "SHIPPING ACCOUNT" list inside the own-account
   * card (prototype `co.shipAccts`). The chosen `selectedShippingAccountId`
   * flows to placement (`CheckoutRequest.shippingAccountId`) and the server
   * resolves it into the frozen own-account carrier/ref. `overseasOnly` renders
   * as a badge but does NOT gate selection (product decision). Empty list →
   * only the "+ Add new shipping account" affordance shows.
   */
  readonly shippingAccounts?: readonly ShippingAccountView[]
  readonly selectedShippingAccountId?: string | null
  readonly onSelectShippingAccount?: (id: string) => void
  readonly onAddShippingAccount?: () => void
}

/**
 * §2 of the single-page checkout (Stage-2b) — "How would you like to ship
 * these parts?" Two mutually-exclusive top-level choices, each a `RadioCard`
 * (`CheckoutView.shippingMethods`, Stage-2a):
 *
 * - "Prepay shipping" — expands (progressive disclosure, same pattern as the
 *   former UID-60 own-account fields) into a rate TABLE with one radio row
 *   per `kind: 'prepay'` method (SHIPPING METHOD / EST. DELIVERY / COST).
 * - "Use my own shipping account" — the single `kind: 'own_account'` method;
 *   carrier-billed, so it carries only its policy note (plus, per UID-128, an
 *   informational carrier-account note when an RFQ seed carried one — see
 *   `seededShipAccountNumber`); the order's ACTUAL carrier/account reference is
 *   resolved server-side once this id is selected regardless — see
 *   checkout-derivation.ts `toShippingMethod`.
 *
 * Selecting any row re-derives the summary's Shipping line server-side —
 * this component only reports the chosen id.
 */
export function ShippingMethodStep({
  methods,
  selectedId,
  onSelect,
  busy = false,
  seededShipAccountNumber,
  shippingAccounts = [],
  selectedShippingAccountId = null,
  onSelectShippingAccount,
  onAddShippingAccount,
}: ShippingMethodStepProps) {
  const prepayMethods = methods.filter((method) => method.kind === 'prepay')
  const ownAccountMethod = methods.find((method) => method.kind === 'own_account')
  const selectedMethod = methods.find((method) => method.id === selectedId)
  const prepaySelected = selectedMethod?.kind === 'prepay'
  const fallbackPrepayId = defaultPrepayMethodId(methods)
  const ownAccountSelected = Boolean(ownAccountMethod) && selectedId === ownAccountMethod?.id

  // §2's "Prepay shipping" row shows the EFFECTIVE prepay cost beside its
  // label (UX spec §2.3 / prototype `co.prepaidCost`) — the currently
  // selected prepay method's cost while prepay is chosen, else the
  // fallback (cheapest) prepay method's cost as a preview.
  const effectivePrepayMethod = prepaySelected
    ? selectedMethod
    : prepayMethods.find((method) => method.id === fallbackPrepayId)

  return (
    <fieldset className="checkout-shipping-method-fieldset" disabled={busy}>
      <legend className="sr-only">Shipping method</legend>
      <RadioCard
        name="shipping-method-kind"
        value="prepay"
        checked={prepaySelected}
        disabled={busy}
        onChange={() => {
          if (!prepaySelected) onSelect(fallbackPrepayId ?? selectedId)
        }}
        label="Prepay shipping"
        trailing={
          effectivePrepayMethod &&
          formatMoney(effectivePrepayMethod.costAmount, effectivePrepayMethod.currency)
        }
      >
        {/*
         * UID-440 (PHASE 3) — the rate rows now share the RFQ quote-option row
         * anatomy (`QuoteVariantSelector.tsx` / `.quote-variant-*` in rfq.css):
         * a bold lead line (method name) + a muted sub-label (est. delivery,
         * folded into this same cell instead of its own column) + a
         * right-aligned cost. Collapsing "Shipping method"/"Est. delivery"
         * into ONE column also removes the old 3-column table's fixed-width
         * middle column, which was the layout most likely to squeeze at
         * narrow viewports — verified (2026-09-02) at 375px there was no
         * actual horizontal-scroll defect on the OLD 3-column layout either
         * (the method column is flexible, not `white-space: nowrap`), so this
         * is anatomy alignment, not a narrow-viewport bugfix; a 2-column table
         * is simply less narrow-viewport-risky than 3, and no separate
         * card-per-row breakpoint is needed on top of it.
         *
         * Kept as a real `<table>` (not a fieldset of `.quote-variant-option`
         * rows) — a semantic 2-column table (method+cost) is still the
         * correct control here (RadioCard/QuotedLinesPanel precedent: table
         * markup for tabular rate data, region-group cards for the RFQ
         * variant picker's very different 2-column REGION topology, which
         * this does NOT adopt — see this file's own top-of-component doc).
         */}
        <div className="checkout-shipping-rate-table-wrap">
          <table className="checkout-shipping-rate-table">
            <caption className="sr-only">Prepay shipping methods</caption>
            <thead>
              <tr>
                <th scope="col">Shipping method</th>
                <th scope="col">Cost</th>
              </tr>
            </thead>
            <tbody>
              {prepayMethods.map((method) => (
                <tr key={method.id} className="checkout-shipping-rate-row">
                  <td>
                    <label className="checkout-shipping-rate-row-label">
                      <input
                        className="radio-card-input checkout-shipping-rate-row-input"
                        type="radio"
                        name="shipping-method-prepay-row"
                        value={method.id}
                        checked={selectedId === method.id}
                        disabled={busy}
                        onChange={() => onSelect(method.id)}
                      />
                      <span className="checkout-shipping-rate-row-body">
                        <span className="checkout-shipping-rate-row-lead">{method.name}</span>
                        <span className="checkout-shipping-rate-row-sublabel">
                          Est. delivery: {formatEstDelivery(method)}
                        </span>
                      </span>
                    </label>
                  </td>
                  <td className="checkout-shipping-rate-row-cost">
                    {formatMoney(method.costAmount, method.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </RadioCard>

      {ownAccountMethod && (
        <RadioCard
          name="shipping-method-kind"
          value={ownAccountMethod.id}
          checked={selectedId === ownAccountMethod.id}
          disabled={busy}
          onChange={() => onSelect(ownAccountMethod.id)}
          label="Use my own shipping account"
          trailing="Billed by carrier"
        >
          {/* UID-206 — the saved SHIPPING ACCOUNT list (prototype `co.shipAccts`),
              shown only while own-account is the selected mode. Extracted to the shared
              `ShippingAccountPicker` (UID-201, ADR-0076) so RFQ compose reuses the same
              control over the same book. `overseasOnly` shows a badge but stays selectable
              (product decision). Empty book → only the "+ Add" affordance. */}
          {ownAccountSelected && onSelectShippingAccount && onAddShippingAccount ? (
            <ShippingAccountPicker
              shippingAccounts={shippingAccounts}
              selectedShippingAccountId={selectedShippingAccountId}
              onSelectShippingAccount={onSelectShippingAccount}
              onAddShippingAccount={onAddShippingAccount}
              busy={busy}
            />
          ) : null}
          {ownAccountSelected && seededShipAccountNumber ? (
            <p className="caption-text checkout-shipping-seed-account-note">
              Carrier account pre-filled from your RFQ: {seededShipAccountNumber}
            </p>
          ) : null}
        </RadioCard>
      )}

      {/* Exact prototype copy (Fictiv Platform.dc.html:2040): a standalone note
          BELOW both option cards, always visible — not nested inside the
          own-account card. */}
      {ownAccountMethod && (
        <p className="caption-text checkout-shipping-policy-note">
          Your company&rsquo;s negotiated rates may differ from Fictiv&rsquo;s estimate above. With
          your own account the carrier bills you directly, subject to Fictiv&rsquo;s{' '}
          <a href="/policies/shipping">Company Shipping Account Policy</a>.
        </p>
      )}
    </fieldset>
  )
}
